import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BookingLayout from 'pages/booking/BookingLayout';
import BookingNotFound from 'pages/booking/BookingNotFound';
import { normalizeAvailableDays } from 'pages/booking/booking-availability';
import MonthView from 'components/MonthView';
import IconArrowLeft from 'assets/arrow-narrow-left.svg';
import IconInfo from 'assets/info.svg';
import IconPlus from 'assets/icon-plus.svg';
import IconMinus from 'assets/icon-minus.svg';
import { IconChevronDown } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import {
  BOOKING_HOME_PATH,
  BOOKING_LOGIN_PATH,
  BOOKING_REGISTRATION_PATH,
  BOOKING_SLUG_CONTACT_PATH,
  BOOKING_SLUG_CREDIT_PURCHASE_PATH,
} from 'routes/paths';
import useLang from 'lang';
import { formatLocaleDate, formatLocaleDateTime, formatLocaleTime } from 'lang/date-time';
import 'pages/booking/BookingDetail.css';

/** Formats year/month/day into a YYYY-MM-DD string. */
function toDateStr(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Formats year/month into a YYYY-MM string for the availability API query. */
function toMonthParam(year: number, month: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

/** Filters the slot list to only those that start on the given calendar day. */
function getSlotsForDay(allSlots: any[], year: number, month: number, day: number) {
  return allSlots.filter((s) => {
    const d = new Date(s.startsAt);
    return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day;
  });
}

/** Builds a 42-cell grid (6 weeks) of day objects for the given month. */
function getCalendarDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const startOffset = firstDay === 0 ? 6 : firstDay - 1;
  const days: { day: number; current: boolean }[] = [];
  for (let i = startOffset - 1; i >= 0; i--) days.push({ day: daysInPrev - i, current: false });
  for (let d = 1; d <= daysInMonth; d++) days.push({ day: d, current: true });
  const rem = 42 - days.length;
  for (let d = 1; d <= rem; d++) days.push({ day: d, current: false });
  return days;
}

/**
 * Detail page for a single reservable space — shows a gallery, description,
 * calendar, time slots, and persons picker before continuing to the contact form.
 */
export default function BookingDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { getAction, jwt, request, setPublicBranding } = useApi();
  const { _t, _tn, lang } = useLang();

  const saved = (() => {
    try {
      return JSON.parse(sessionStorage.getItem('bookingState') ?? 'null');
    } catch {
      return null;
    }
  })();
  const savedForThis = saved?.service?.slug === slug ? saved : null;

  const today = new Date();
  const initDate = savedForThis?.dateRaw ? new Date(savedForThis.dateRaw) : today;

  const [space, setSpace] = useState<any>(null);
  const [spaceLoading, setSpaceLoading] = useState(true);
  const [spaceError, setSpaceError] = useState<string | null>(null);
  const [spaceNotFound, setSpaceNotFound] = useState(false);
  const [slots, setSlots] = useState<any[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [needsJoin, setNeedsJoin] = useState(false);
  const [joining, setJoining] = useState(false);

  const [imgIndex, setImgIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [creditBundlesExpanded, setCreditBundlesExpanded] = useState(false);
  const [calYear, setCalYear] = useState(initDate.getFullYear());
  const [calMonth, setCalMonth] = useState(initDate.getMonth());
  const [selectedDay, setSelectedDay] = useState<number | null>(savedForThis?.dateRaw ? initDate.getDate() : null);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [numSlots, setNumSlots] = useState(1);
  const [persons, setPersons] = useState(savedForThis?.persons ?? 1);

  const availableDays = normalizeAvailableDays(space?.availableDays);
  const images: string[] = space?.imageUrl ? [space.imageUrl] : [];
  const maxPersons = selectedSlot ? Math.min(space.maxPersons, selectedSlot.availablePersons) : space?.maxPersons ?? 10;
  const hasNoCredits =
    Boolean(jwt) && space?.paymentMode === 'credit' && Number(space.creditBalance?.available ?? 0) <= 0;
  const anonymousBookingUnavailable =
    !jwt &&
    Boolean(space) &&
    (space.forceGuestRegistration === true || space.paymentMode !== 'buyable-credits' || space.openForGuests !== true);
  const registrationOnly = anonymousBookingUnavailable && space?.guestRegistrationEnabled === true;
  const guestCannotBook = anonymousBookingUnavailable && !registrationOnly;

  useEffect(() => {
    let cancelled = false;
    setSpaceLoading(true);
    setSpaceError(null);
    setSpaceNotFound(false);
    setNeedsJoin(false);
    setCreditBundlesExpanded(false);
    getAction('GetPublicReservableSpace')({ parameters: { slug: slug! } })
      .then(async (data: any) => {
        if (!cancelled) {
          let authenticated = null;
          if (jwt) {
            if (data.space.authenticatedBookingAccess === false) {
              if (!data.space.joinEligible) {
                setSpaceError(_t('This account cannot join or book this space.'));
                setSpaceLoading(false);
                return;
              }
              setNeedsJoin(true);
            } else {
              try {
                authenticated = await getAction('GetReservableSpace')({ parameters: { id: data.space.id } });
              } catch (error) {
                if (!data.space.guestRegistrationEnabled || data.space.joinEligible === false) throw error;
                setNeedsJoin(true);
              }
            }
          }
          if (cancelled) return;
          setSpace({ ...data.space, ...(authenticated as any)?.space });
          setPublicBranding({
            buildingId: data.building.id,
            logoUrl: data.building.logoUrl ?? null,
            buildingName: data.building.title ?? null,
          });
          setSpaceLoading(false);
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          if (err.response?.status === 404) {
            setSpaceNotFound(true);
          } else {
            setSpaceError(err.message);
          }
          setSpaceLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, jwt]);

  useEffect(() => {
    if (!slug || !space || guestCannotBook || registrationOnly || needsJoin || hasNoCredits) return;
    let cancelled = false;
    setSlotsLoading(true);
    const availabilityRequest = jwt
      ? getAction('GetSpaceAvailability')({
          parameters: { id: space.id },
          query: { month: toMonthParam(calYear, calMonth) },
        })
      : getAction('GetPublicSpaceAvailability')({
          parameters: { slug },
          query: { month: toMonthParam(calYear, calMonth) },
        });
    availabilityRequest
      .then((data: any) => {
        if (!cancelled) {
          setSlots(data.slots ?? []);
          if (jwt && data.creditBalance) {
            setSpace((current: any) => ({ ...current, creditBalance: data.creditBalance }));
          }
          setSlotsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setSlotsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, calYear, calMonth, jwt, space?.id, guestCannotBook, registrationOnly, needsJoin, hasNoCredits]);

  /** Add the signed-in booking guest to this space. */
  const joinSpace = async () => {
    setJoining(true);
    try {
      await request({ method: 'POST', url: `/reservations/spaces/${space.id}/join` });
      setNeedsJoin(false);
      window.location.reload();
    } finally {
      setJoining(false);
    }
  };

  const calDays = getCalendarDays(calYear, calMonth);
  const daySlots = selectedDay === null ? [] : getSlotsForDay(slots, calYear, calMonth, selectedDay);

  // Restore selected slot and numSlots from saved booking state once slots are loaded
  useEffect(() => {
    if (!savedForThis || slotsLoading || selectedSlot) return;
    const match = daySlots.find((s) => s.startsAt === savedForThis.slot?.startsAt);
    if (match && space) {
      setSelectedSlot(match);
      const slotDuration = space.slotDurationMinutes ?? 30;
      const totalMinutes =
        (new Date(savedForThis.slot.endsAt).getTime() - new Date(savedForThis.slot.startsAt).getTime()) / 60000;
      setNumSlots(Math.max(1, Math.round(totalMinutes / slotDuration)));
    }
  }, [daySlots, slotsLoading]);

  const maxSlotsPerReservation: number = space?.maxSlotsPerReservation ?? 1;
  const availableCredits = Number(space?.creditBalance?.available ?? 0);
  const isCreditSpace = space?.paymentMode === 'credit';
  const isBuyableCreditSpace = space?.paymentMode === 'buyable-credits';
  const isCreditEnabledSpace = isCreditSpace || isBuyableCreditSpace;
  const showCredits = Boolean(jwt) && isCreditEnabledSpace;
  const creditsApplied = showCredits ? Math.min(numSlots, availableCredits) : 0;
  const paidSlots = isBuyableCreditSpace ? numSlots - creditsApplied : 0;
  const pricePerSlotWithVat = Number(space?.pricePerSlotWithVat ?? 0);
  const currencyCode = space?.currencyCode ?? 'EUR';
  const grossPrice = paidSlots * pricePerSlotWithVat;
  const vatPercentage = Number(space?.vatPercentage ?? 0);
  const netPrice = vatPercentage ? grossPrice / (1 + vatPercentage / 100) : grossPrice;
  const vat = grossPrice - netPrice;
  const selectableSlotLimit = isCreditSpace
    ? Math.min(maxSlotsPerReservation, availableCredits)
    : maxSlotsPerReservation;
  const selectedSlotIndex = selectedSlot ? daySlots.findIndex((s) => s === selectedSlot) : -1;
  const endSlot = selectedSlotIndex >= 0 ? daySlots[selectedSlotIndex + numSlots - 1] ?? null : null;

  // Count how many consecutive available slots exist from the selected slot
  const maxExtendable =
    selectedSlotIndex >= 0
      ? (() => {
          let count = 0;
          for (let i = selectedSlotIndex; i < daySlots.length && count < selectableSlotLimit; i++) {
            const s = daySlots[i];
            if (!s.isAvailable || new Date(s.startsAt) <= new Date() || s.availablePersons < persons) break;
            count++;
          }
          return count;
        })()
      : 1;

  /** Moves the calendar to the previous month. */
  const prevMonth = () => {
    setSelectedDay(null);
    setSelectedSlot(null);
    if (calMonth === 0) {
      setCalMonth(11);
      setCalYear((y) => y - 1);
    } else setCalMonth((m) => m - 1);
  };
  /** Moves the calendar to the next month. */
  const nextMonth = () => {
    setSelectedDay(null);
    setSelectedSlot(null);
    if (calMonth === 11) {
      setCalMonth(0);
      setCalYear((y) => y + 1);
    } else setCalMonth((m) => m + 1);
  };

  /** Increments or decrements the persons count, clamped to [1, maxPersons]. */
  const handlePersonsChange = (delta: number) => {
    const next = Math.max(1, Math.min(persons + delta, maxPersons));
    setPersons(next);
    if (selectedSlot && selectedSlot.availablePersons < next) {
      setSelectedSlot(null);
      setNumSlots(1);
    }
  };

  /** Navigates to the contact form with the selected slot and booking details. */
  const handleContinue = () => {
    if (!selectedSlot || !endSlot || selectedDay === null) return;
    const bookingState = {
      service: {
        id: space.id,
        slug: space.slug,
        title: space.name,
        description: space.description,
        autoAcceptReservations: space.autoAcceptReservations,
        paymentMode: space.paymentMode,
        openForGuests: space.openForGuests,
        paymentGateway: space.paymentGateway,
        pricePerSlotWithVat,
        vatPercentage,
        currencyCode,
        billingAddress: space.billingAddress,
        creditBalance: space.creditBalance,
      },
      slot: { startsAt: selectedSlot.startsAt, endsAt: endSlot.endsAt },
      date: formatLocaleDate(new Date(calYear, calMonth, selectedDay), lang),
      dateRaw: toDateStr(calYear, calMonth, selectedDay),
      time: `${formatLocaleTime(selectedSlot.startsAt, lang)} – ${formatLocaleTime(endSlot.endsAt, lang)}`,
      persons,
      slotCount: numSlots,
      creditCost: showCredits ? creditsApplied : 0,
      paidSlots,
      grossPrice,
      vat,
      netPrice,
      projectedCredits: showCredits ? availableCredits - creditsApplied : null,
      isGuest: !jwt,
    };
    sessionStorage.setItem('bookingState', JSON.stringify(bookingState));
    navigate(BOOKING_SLUG_CONTACT_PATH.insert({ slug: space.slug }), { state: bookingState });
  };

  if (spaceLoading) {
    return (
      <BookingLayout>
        <div className="detail-page">
          <div className="detail-inner">
            <p>{_t('Loading...')}</p>
          </div>
        </div>
      </BookingLayout>
    );
  }

  if (spaceNotFound || guestCannotBook) {
    return <BookingNotFound />;
  }

  if (spaceError || !space) {
    return (
      <BookingLayout>
        <div className="detail-page">
          <div className="detail-inner">
            <button className="back-btn" onClick={() => navigate(BOOKING_HOME_PATH.original)}>
              <img src={IconArrowLeft} width={20} height={20} alt="" />
              {_t('Home')}
            </button>
            <p style={{ marginTop: '2rem', color: 'var(--bk-error)' }}>{spaceError || _t('Space not found.')}</p>
          </div>
        </div>
      </BookingLayout>
    );
  }

  return (
    <BookingLayout>
      <div className="detail-page">
        <div className="detail-inner">
          <button className="back-btn" onClick={() => navigate(BOOKING_HOME_PATH.original)}>
            <img src={IconArrowLeft} width={20} height={20} alt="" />
            {_t('Home')}
          </button>

          <div className="detail-content">
            {images.length > 0 && (
              <div className="detail-carousel-wrap">
                <div className="detail-carousel">
                  <img src={images[imgIndex]} alt={space.name} className="detail-carousel-img" />
                  {images.length > 1 && (
                    <>
                      <button
                        className="carousel-arrow carousel-arrow--left"
                        onClick={() => setImgIndex((i) => (i - 1 + images.length) % images.length)}
                      >
                        ‹
                      </button>
                      <button
                        className="carousel-arrow carousel-arrow--right"
                        onClick={() => setImgIndex((i) => (i + 1) % images.length)}
                      >
                        ›
                      </button>
                      <div className="carousel-dots">
                        {images.map((_, i) => (
                          <button
                            key={i}
                            className={`carousel-dot${i === imgIndex ? ' active' : ''}`}
                            onClick={() => setImgIndex(i)}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="detail-info">
              <h1 className="detail-title">{space.name}</h1>
              <p className="detail-location">
                {space.availableFrom} – {space.availableTo}
              </p>

              <div className="detail-desc">
                <p className={expanded ? '' : 'detail-desc-clamp'}>{space.description}</p>
                {(space.description?.length ?? 0) > 300 && (
                  <button className="detail-read-more" onClick={() => setExpanded((v) => !v)}>
                    {expanded ? `- ${_t('Read less')}` : `+ ${_t('Read more')}`}
                  </button>
                )}
              </div>

              {isBuyableCreditSpace && (space.creditProducts?.length ?? 0) > 0 && (
                <div className="booking-section booking-credit-products" data-testid="credit-products">
                  <div className="booking-credit-products-heading">
                    <div>
                      <h2>{_t('Credit bundles')}</h2>
                      <p>{_t('Credit bundles offer a better price than booking each visit separately.')}</p>
                    </div>
                    <button
                      type="button"
                      className="booking-credit-products-toggle"
                      aria-expanded={creditBundlesExpanded}
                      aria-controls="booking-credit-products-content"
                      onClick={() => setCreditBundlesExpanded((current) => !current)}
                    >
                      <span>{_t(creditBundlesExpanded ? 'Hide credit bundles' : 'View credit bundles')}</span>
                      <IconChevronDown
                        aria-hidden="true"
                        className={`booking-credit-products-chevron${
                          creditBundlesExpanded ? ' booking-credit-products-chevron--expanded' : ''
                        }`}
                        data-testid="credit-bundles-chevron"
                        size={18}
                        stroke={2.25}
                      />
                    </button>
                  </div>
                  <div
                    id="booking-credit-products-content"
                    className={`booking-credit-products-content${
                      creditBundlesExpanded ? ' booking-credit-products-content--expanded' : ''
                    }`}
                    aria-hidden={!creditBundlesExpanded}
                    data-testid="credit-products-content"
                  >
                    <div className="booking-credit-products-content-inner">
                      <div className="booking-credit-product-list">
                        {space.creditProducts.map((product: any) => (
                          <div className="booking-credit-product" key={product.id}>
                            <strong>
                              {_tn('%d credit', '%d credits', Number(product.credits)).replace(
                                '%d',
                                String(product.credits)
                              )}
                            </strong>
                            <span>
                              {Number(product.priceWithVat).toFixed(2)} {product.currencyCode}
                            </span>
                            <small>
                              {_t('Expires %d days after payment').replace('%d', String(product.expiresInDays))}
                            </small>
                            {jwt && (
                              <button
                                className="account-button"
                                onClick={() =>
                                  navigate(
                                    `${BOOKING_SLUG_CREDIT_PURCHASE_PATH.insert({ slug: space.slug })}?productId=${
                                      product.id
                                    }`,
                                    { state: { space } }
                                  )
                                }
                              >
                                {_t('Buy credits')}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      {!jwt && (
                        <div className="booking-credit-guest-cta">
                          <p className="booking-note">{_t('Sign in to buy credits for future bookings.')}</p>
                          <div className="booking-credit-guest-actions">
                            <button
                              type="button"
                              className="cta-btn"
                              onClick={() =>
                                navigate(`${BOOKING_LOGIN_PATH.original}?slug=${encodeURIComponent(space.slug)}`)
                              }
                            >
                              {_t('Log in')}
                            </button>
                            {space.guestRegistrationEnabled && (
                              <button
                                type="button"
                                className="cta-btn cta-btn--secondary"
                                onClick={() => navigate(BOOKING_REGISTRATION_PATH.insert({ slug: space.slug }))}
                              >
                                {_t('Register')}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {registrationOnly || needsJoin ? (
                <div className="booking-section booking-credit-warning">
                  <p>
                    {needsJoin
                      ? _t('Join this space to make reservations.')
                      : _t('Create a booking account to reserve this space.')}
                  </p>
                  <button
                    className="cta-btn"
                    disabled={joining}
                    onClick={() =>
                      needsJoin ? joinSpace() : navigate(BOOKING_REGISTRATION_PATH.insert({ slug: space.slug }))
                    }
                  >
                    {joining ? _t('Joining...') : needsJoin ? _t('Join space') : _t('Create booking account')}
                  </button>
                </div>
              ) : hasNoCredits ? (
                <div className="booking-section booking-credit-warning" role="alert">
                  {_t('You have no credits left to reserve this space.')}
                </div>
              ) : (
                <>
                  {showCredits && (
                    <div className="booking-section booking-credit-card" data-testid="credit-balance-card">
                      <div className="booking-credit-content">
                        <strong>
                          {_t('Available credits')}: {availableCredits}
                        </strong>
                        {space.creditBalance?.expirations?.map((expiration: any) => (
                          <span key={expiration.expiresAt} style={{ display: 'block' }}>
                            {_tn('%d credit expires on %s', '%d credits expire on %s', Number(expiration.credits))
                              .replace('%d', String(expiration.credits))
                              .replace('%s', formatLocaleDateTime(expiration.expiresAt, lang))}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="booking-section">
                    <label className="booking-label">{_t('Select date')}</label>
                    <MonthView
                      calYear={calYear}
                      calMonth={calMonth}
                      calDays={calDays}
                      selectedDay={selectedDay}
                      today={today}
                      availableDays={availableDays}
                      slots={slots}
                      loading={slotsLoading}
                      bookedDates={[]}
                      onPrev={prevMonth}
                      onNext={nextMonth}
                      onSelect={(y, m, d) => {
                        setCalYear(y);
                        setCalMonth(m);
                        setSelectedDay(d);
                        setSelectedSlot(null);
                      }}
                    />
                  </div>

                  <div className="booking-section">
                    <label className="booking-label">{_t('Select time')}</label>
                    {slotsLoading ? (
                      <p className="booking-note">{_t('Loading...')}</p>
                    ) : daySlots.length === 0 ? (
                      <p className="booking-note">{_t('Reservations are not possible for this day.')}</p>
                    ) : (
                      <div className="time-slots">
                        {daySlots.map((slot, i) => {
                          const isPast = new Date(slot.startsAt) <= new Date();
                          const unavailable =
                            isPast || !slot.isAvailable || slot.availablePersons < persons || selectableSlotLimit === 0;
                          if (unavailable) return null;
                          const isInRange =
                            selectedSlotIndex >= 0 && i >= selectedSlotIndex && i < selectedSlotIndex + numSlots;
                          return (
                            <button
                              key={i}
                              className={`time-slot${isInRange ? ' time-slot--selected' : ''}`}
                              onClick={() => {
                                let availableFromSlot = 0;
                                for (let j = i; j < daySlots.length && availableFromSlot < selectableSlotLimit; j++) {
                                  const candidate = daySlots[j];
                                  if (
                                    !candidate.isAvailable ||
                                    new Date(candidate.startsAt) <= new Date() ||
                                    candidate.availablePersons < persons
                                  ) {
                                    break;
                                  }
                                  availableFromSlot++;
                                }
                                setSelectedSlot(slot);
                                setNumSlots((current) => Math.max(1, Math.min(current, availableFromSlot)));
                              }}
                            >
                              {formatLocaleTime(slot.startsAt, lang)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <p className="booking-note">
                      <img src={IconInfo} className="booking-note-icon" alt="" />
                      <span>
                        {_t('Maximum reservation length: %d minutes.').replace(
                          '%d',
                          String(maxSlotsPerReservation * (space.slotDurationMinutes ?? 30))
                        )}
                        <br />
                        {_t('Occupied slots are not displayed.')}
                      </span>
                    </p>
                  </div>

                  {selectedSlot && maxSlotsPerReservation > 1 && (
                    <div className="booking-section">
                      <label className="booking-label">{_t('Reservation length')}</label>
                      <div className="persons-control">
                        <button
                          className="persons-btn"
                          aria-label={_t('Decrease reservation length')}
                          onClick={() => setNumSlots((n) => Math.max(1, n - 1))}
                          disabled={numSlots <= 1}
                        >
                          <img src={IconMinus} alt="-" />
                        </button>
                        <span className="persons-value">
                          {numSlots * (space.slotDurationMinutes ?? 30)} {_t('min')}
                        </span>
                        <button
                          className="persons-btn"
                          aria-label={_t('Increase reservation length')}
                          onClick={() => setNumSlots((n) => Math.min(maxExtendable, n + 1))}
                          disabled={numSlots >= maxExtendable}
                        >
                          <img src={IconPlus} alt="+" />
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="booking-section">
                    <label className="booking-label">{_t('Number of persons')}</label>
                    <div className="persons-control">
                      <button className="persons-btn" onClick={() => handlePersonsChange(-1)}>
                        <img src={IconMinus} alt="-" />
                      </button>
                      <span className="persons-value">{persons}</span>
                      <button
                        className="persons-btn"
                        onClick={() => handlePersonsChange(1)}
                        disabled={persons >= maxPersons}
                      >
                        <img src={IconPlus} alt="+" />
                      </button>
                    </div>
                    {selectedSlot && (
                      <p className="booking-note" style={{ marginTop: '0.5rem' }}>
                        {_t('Available spots: %d / %d')
                          .replace('%d', String(selectedSlot.availablePersons))
                          .replace('%d', String(selectedSlot.totalPersons))}
                      </p>
                    )}
                  </div>

                  {selectedSlot && isCreditEnabledSpace && (
                    <div className="booking-section booking-cost-summary-card" data-testid="cost-summary-card">
                      <h2 className="booking-cost-summary-title">{_t('Cost summary')}</h2>
                      <div className="booking-cost-summary-rows">
                        {showCredits && (
                          <div className="booking-cost-summary-row">
                            <span>{_t('Credit cost')}</span>
                            <strong>{creditsApplied}</strong>
                          </div>
                        )}
                        {isBuyableCreditSpace && (
                          <div className="booking-cost-summary-row">
                            <span>{_t('Price including VAT')}</span>
                            <strong>
                              {grossPrice.toFixed(2)} {currencyCode}
                            </strong>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <button className="cta-btn" onClick={handleContinue} disabled={!selectedSlot}>
                    {_t('Continue to complete reservation')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </BookingLayout>
  );
}
